import React, { useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck, Shield } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

const RISK_STYLE = { high: "bg-red-100 text-red-700", medium: "bg-amber-100 text-amber-700", low: "bg-[#138808]/10 text-[#138808]" };
const RISK_ICON = { high: ShieldAlert, medium: Shield, low: ShieldCheck };

export default function AntiCheat() {
  const [rows, setRows] = useState([]);
  const [timeline, setTimeline] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/anticheat");
      setRows(data.participants);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const openTimeline = async (userId, name) => {
    try {
      const { data } = await api.get(`/admin/anticheat/${userId}`);
      setTimeline({ name, events: data.events });
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (userId, status) => {
    if (!window.confirm(`Set participant status to "${status}"?`)) return;
    try {
      await api.patch(`/admin/participants/${userId}`, { status });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-anticheat">
      <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Anti-Cheat Monitor</h1>
      <p className="text-sm text-slate-500 mt-1">Risk = weighted score of tab switches, copy attempts, rapid answers and request anomalies.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-[10px] font-bold tracking-[0.15em] uppercase text-slate-400 border-b border-slate-200">
              <th className="text-left px-5 py-3">Player</th><th className="text-left px-5 py-3">Risk</th><th className="text-left px-5 py-3">Events</th><th className="text-left px-5 py-3">Tab Switches</th><th className="text-left px-5 py-3">Last Event</th><th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => {
              const Icon = RISK_ICON[r.risk_level] || Shield;
              return (
                <tr key={r.user_id} className="hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => openTimeline(r.user_id, r.display_name)} data-testid={`risk-row-${r.user_id}`}>
                  <td className="px-5 py-3 font-semibold text-slate-800">{r.display_name}</td>
                  <td className="px-5 py-3"><span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${RISK_STYLE[r.risk_level]}`}><Icon className="w-3 h-3" /> {r.risk_level} ({r.risk_score})</span></td>
                  <td className="px-5 py-3 font-timer font-bold text-slate-600">{r.events}</td>
                  <td className="px-5 py-3 font-timer font-bold text-slate-600">{r.tab_switches}</td>
                  <td className="px-5 py-3 text-xs text-slate-400">{r.last ? new Date(r.last).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</td>
                  <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {r.status !== "disqualified" ? (
                      <button onClick={() => setStatus(r.user_id, "disqualified")} data-testid={`disqualify-${r.user_id}`} className="text-xs font-bold text-red-600 hover:underline">Disqualify</button>
                    ) : (
                      <button onClick={() => setStatus(r.user_id, "approved")} data-testid={`reinstate-${r.user_id}`} className="text-xs font-bold text-[#138808] hover:underline">Reinstate</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-400" data-testid="anticheat-empty">No anti-cheat events recorded. Competition is clean.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!timeline} onOpenChange={() => setTimeline(null)}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="anticheat-timeline-modal">
          <DialogHeader><DialogTitle className="font-heading">Event Timeline — {timeline?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            {timeline?.events?.map((e) => (
              <div key={e.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2.5 text-sm">
                <div>
                  <span className="font-semibold text-slate-800">{e.type.replace(/_/g, " ")}</span>
                  {e.category_id && <span className="text-xs text-slate-400 ml-2">{e.category_id}</span>}
                </div>
                <span className="text-xs text-slate-400">{new Date(e.ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
              </div>
            ))}
            {timeline?.events?.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No events.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
